import * as React from 'react';
import Section from '../../components/Section';
import GuideStepRow from '../../components/GuideStepRow';
import { GuideStep, Guide, GuideDose, StepType } from '../../guide';
import { Flash } from '../../components/Flash';
import uuid = require('uuid');
import { Button } from '../../components/Button';
import { NumberField } from '../../components/forms';

const NEW_STEP: GuideStep = {
  id: '',
  name: '',
  description_text: null,
  image_identifier: null,
  amount: 0,
  amount_unit: 'g',
  time_at: 0,
  time_duration: 0,
  type: null,
};

const canAddBloomStep = (steps: GuideStep[], remaining: number): boolean => {
  if (!steps.length) {
    return true;
  }

  if (remaining <= 0) {
    return false;
  }

  if (!steps.length) {
    return true;
  }

  return steps.find((item: GuideStep) => item.type === StepType.bloom) === null;
};

const canAddPourStep = (steps: GuideStep[], remaining: number): boolean => {
  if (remaining <= 0) {
    return false;
  }

  if (steps.find((item: GuideStep) => item.type === StepType.bloom) === null) {
    return false;
  }

  const last = [...steps]
    .reverse()
    .find((item: GuideStep) => item.type !== (null as StepType));

  if (!last) {
    return false;
  }

  return last.type !== StepType.bloom && last.type !== StepType.pour;
};

const remainingWater = (steps: GuideStep[], dose: GuideDose): number => {
  return (
    dose.water -
    steps.reduce((sum: number, step: GuideStep) => sum + step.amount, 0)
  );
};

type Props = {
  dose: GuideDose;
  guide: Guide;
  steps: GuideStep[];
  onChange: Function;
};

const setStep = (props: Props, setIsAdding: Function) => {
  console.log('setStep.render');
  return (index: number, newStep: GuideStep) => {
    console.log('setStep.action');
    const newSteps = props.steps.slice();

    if (!newStep.id) {
      newStep.id = uuid.v4();
    }

    if (index < newSteps.length) {
      newSteps[index] = newStep;
    } else {
      newSteps.push(newStep);
    }

    setIsAdding(false);

    props.onChange(newSteps);
  };
};

const removeStep = (props: Props, index: number) => () => {
  console.log('REMOVE');
  props.onChange(props.steps.filter((_, stepIndex) => index !== stepIndex));
};

const StepWrapper = ({ canReorder, onValueChange, index, children }: any) => {
  return (
    <div className="flex items-center">
      {canReorder && (
        <div className="mr3">
          <NumberField
            label="Step Number"
            field={['index']}
            id="index"
            defaultValue={`${index + 1}`}
            onValueChange={(_: any, value: any) =>
              onValueChange(index, parseInt(value, 10) - 1)
            }
          />
        </div>
      )}
      <div className="flex-auto">{children}</div>
    </div>
  );
};

const moveStep = (arr: any[], from: number, to: number): any[] => {
  return arr.splice(to, 0, arr.splice(from, 1)[0]);
};

export default function GuideStepList(props: Props) {
  const value = remainingWater(props.steps, props.dose);
  const [isAdding, setIsAdding] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [pos, setPos] = React.useState([]);

  React.useEffect(() => {
    setPos(
      props.steps.reduce((obj: any, item: any, index: number) => {
        obj[index] = index;
        return obj;
      }, []),
    );
  }, [props]);

  const canAddBloom = canAddBloomStep(props.steps, value);
  const canAddPour = canAddPourStep(props.steps, value);

  return (
    <Section title="Steps">
      {props.steps.length === 0 && (
        <Flash
          text={
            'Start off by adding a `Bloom` step. All preparation will be instructed through Filtru'
          }
        />
      )}
      {props.steps.map((step: any, index: number) => (
        <StepWrapper
          key={index}
          canReorder={isEditing}
          index={pos[index]}
          onValueChange={(key: any, newKey: any) => {
            if (key && newKey) {
              const newPos = pos.slice();
              newPos[key] = newKey;
              setPos(newPos);
            }
          }}
        >
          <GuideStepRow
            key={index}
            onChange={setStep(props, setIsAdding)}
            onDelete={removeStep(props, index)}
            index={index}
            step={step}
            canAddBloomStep={canAddBloom}
            canAddPourStep={canAddPour}
          />
        </StepWrapper>
      ))}
      {isAdding ? (
        <GuideStepRow
          isEditing={true}
          key={props.steps.length}
          onChange={setStep(props, setIsAdding)}
          onDelete={removeStep(props, props.steps.length)}
          index={props.steps.length}
          step={Object.assign({}, NEW_STEP)}
          canAddBloomStep={canAddBloom}
          canAddPourStep={canAddPour}
        />
      ) : null}
      <div className="flex-ns justify-center items-center mt3">
        {props.steps.length > 1 && (
          <Button
            onClick={() => {
              if (isEditing) {
                // Re-order…
                Object.keys(pos).forEach((key: any) => {
                  const newSteps = props.steps.slice();

                  moveStep(newSteps, key, pos[key]);

                  props.onChange(newSteps);
                });
              }

              setIsEditing(!isEditing);
            }}
          >
            {isEditing ? 'Done' : 'Re-order'}
          </Button>
        )}
        {!isEditing && (
          <Button onClick={() => setIsAdding(true)}>Add Step</Button>
        )}
        <span className="flex-auto black-70 b db link bg-transparent bw0 tr">
          Remaining water amount: {value}g
        </span>
      </div>
    </Section>
  );
}
